/**
 * THE BAKED MODEL RENDER OF A UNIT, FOR THE META SCREENS.
 *
 * Every unit whose 3D model has been through the pipeline has a cut-out under
 * `frontend/public/sprites/heroes/<cardId>.png` — a transparent render of the
 * GLB, 256 px tall, width whatever the silhouette needs (98 to 390 px today).
 * 27 of them also have `<cardId>-back.png`, the same model seen from behind.
 * Those two files are the STABLE artefact: they are what the match renders a
 * deployed unit with (`engine/match/render/entities.ts`), they live in
 * `public/`, and they ship in the image.
 *
 * They are NOT the only render of a unit in the tree, and the other one is a
 * trap. `frontend/public/dev/anim/<cardId>/<clip>/fNNN.png` holds full
 * animation frames for 38 units — but that tree is a DEV PREVIEW for
 * `public/dev/heroes.html`, it is not part of the product, and it may be
 * deleted at any time. Nothing that a player can reach may depend on it. So
 * this module resolves the cut-outs and nothing else.
 *
 * ── WHY THE IDS ARE A LIST AND NOT A FETCH ──────────────────────────────────
 * There is no manifest to read. `public/hero-index.json` exists but is written
 * by hand for the dev page and read by that page alone, so depending on it
 * would make a shipping screen depend on a dev artefact for the second time.
 * A static frozen set costs one line per unit and cannot 404.
 *
 * Being wrong in the "listed but absent" direction is the one that shows: the
 * card detail would mount a box for art that does not exist. `createMediaSlot`
 * degrades that to the placeholder rather than a broken frame, but the box
 * would still be there, so `test/hero-model-art.test.ts` reads the real
 * directory and fails if this list and the files on disk disagree in EITHER
 * direction. Drop a new cut-out in and that test tells you to add its id here.
 *
 * ── LAYER (ADR-0005) ────────────────────────────────────────────────────────
 * META. This file must not import `@/engine/match/**` — which is exactly why
 * the id set is duplicated from `HERO_TEST_IDS` in
 * `engine/match/render/entities.ts` rather than imported from it, the same
 * trade `engine/ui/card-art.ts` makes with the asset manifest's types. The
 * test above pins both copies to the same directory listing, so the duplicate
 * cannot silently drift.
 */

/** Where the cut-outs are served from, in dev and in the built image. */
export const HERO_MODEL_BASE = '/sprites/heroes/';

/**
 * Every card id with a baked front cut-out. Units only — spells and structures
 * have no model, and `heroModelUrl` returns `''` for them.
 *
 * Sorted, so a diff of this list reads as "one unit arrived".
 */
export const HERO_MODEL_IDS: ReadonlySet<string> = Object.freeze(
  new Set([
    'ember-cinderhound',
    'ember-emberkite',
    'ember-forgetitan',
    'ember-kilnmortar',
    'ember-scorchveil',
    'ember-slagbrute',
    'ember-tinderling',
    'hollow-cairnling',
    'hollow-charnel-ox',
    'hollow-dirgecaller',
    'hollow-gravebell',
    'hollow-marrowling',
    'hollow-pallwisp',
    'hollow-reliquarch',
    'hollow-shivshade',
    'hollow-urnbearer',
    'hollow-wickfang',
    'neutral-anvilback',
    'neutral-arcwright',
    'neutral-ironward',
    'neutral-kitehook',
    'neutral-mote-runner',
    'neutral-quietstep',
    'neutral-rivetling',
    'neutral-vaultbreaker',
    'thorn-barbsling',
    'thorn-bramblekin',
    'thorn-briarwhelp',
    'thorn-dewtender',
    'thorn-elderbriar',
    'thorn-gallmoths',
    'thorn-heartwood',
    'thorn-nettlings',
    'tide-anchorstone',
    'tide-brackling',
    'tide-brinecaster',
    'tide-brinewarden',
    'tide-rimegill',
    'tide-rimeray',
    'tide-wrackwalker',
  ]),
);

/**
 * The subset that also has a rear view. A card in here is always in
 * `HERO_MODEL_IDS` too — the test asserts it, because a back with no front
 * would render a lone rear view of something the player never saw from ahead.
 */
export const HERO_MODEL_BACK_IDS: ReadonlySet<string> = Object.freeze(
  new Set([
    'ember-cinderhound',
    'ember-emberkite',
    'ember-forgetitan',
    'ember-kilnmortar',
    'ember-scorchveil',
    'ember-slagbrute',
    'ember-tinderling',
    'hollow-charnel-ox',
    'hollow-dirgecaller',
    'hollow-pallwisp',
    'hollow-reliquarch',
    'hollow-urnbearer',
    'neutral-anvilback',
    'neutral-arcwright',
    'neutral-ironward',
    'neutral-kitehook',
    'neutral-mote-runner',
    'thorn-barbsling',
    'thorn-bramblekin',
    'thorn-dewtender',
    'thorn-elderbriar',
    'thorn-heartwood',
    'thorn-nettlings',
    'tide-anchorstone',
    'tide-brinewarden',
    'tide-rimegill',
    'tide-wrackwalker',
  ]),
);

/** True when this card has a baked model render to show. */
export function hasHeroModel(cardId: string): boolean {
  return HERO_MODEL_IDS.has(cardId);
}

/**
 * URL of the front view, or `''` when this card has no model art.
 *
 * `''` rather than null so a caller can hand it straight to
 * `MediaSlot.setSrc` semantics without a branch, exactly like
 * `CardArt.faceUrl`.
 */
export function heroModelUrl(cardId: string): string {
  return HERO_MODEL_IDS.has(cardId) ? `${HERO_MODEL_BASE}${cardId}.png` : '';
}

/** URL of the rear view, or `''` when this card has none. */
export function heroModelBackUrl(cardId: string): string {
  return HERO_MODEL_BACK_IDS.has(cardId) ? `${HERO_MODEL_BASE}${cardId}-back.png` : '';
}

/** Both views of one card, front first, `''` entries dropped. */
export function heroModelViews(cardId: string): readonly string[] {
  return [heroModelUrl(cardId), heroModelBackUrl(cardId)].filter((url) => url !== '');
}
